import React from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import type { TooltipItem } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import type { UnitsByBrandItem } from "../types";
import { generateBrandColors } from "../config/colors";

interface DonutChartProps {
  data: UnitsByBrandItem[];
  title?: string;
  colors?: string[];
}

// Register necessary Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const enableChartDebug = import.meta.env.VITE_ENABLE_CHART_DEBUG === "true";

const toFiniteNumber = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

function validateChartData(
  data: UnitsByBrandItem[],
  componentName = "DonutChart",
) {
  const issues = [];

  if (!data || data.length === 0) {
    issues.push("No data provided");
  }

  if (data) {
    const invalidItems = data.filter(
      (item) => !item.brand_name || item.units < 0,
    );
    if (invalidItems.length > 0) {
      issues.push(
        `${invalidItems.length} items have invalid brand_name or negative units`,
      );
    }
  }

  if (issues.length > 0 && process.env.NODE_ENV === "development") {
    console.error(`${componentName} Data Issues:`, issues);
    console.table(data);
  }

  return issues.length === 0;
}

export const DonutChart: React.FC<DonutChartProps> = ({
  data,
  title,
  colors,
}) => {
  // Validate chart data
  const isValidData = validateChartData(data, "DonutChart");

  if (!isValidData || !data || data.length === 0) {
    return (
      <div className="w-full">
        {title && (
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            {title}
          </h3>
        )}
        <div className="h-56 w-full flex items-center justify-center text-gray-500">
          No data available
        </div>
      </div>
    );
  }

  const labels = data.map((item) => item.brand_name);
  const units = data.map((item) => item.units);

  // Generate dynamic colors for all brands using centralized config
  const brandColorMap = generateBrandColors(labels, true);
  const backgroundColors = labels.map((brandName, index) => {
    const customColor = colors?.[index];
    return customColor ?? brandColorMap[brandName];
  });

  if (enableChartDebug) {
    console.group("[CHART] DonutChart Data Debug");
    console.log("Brand Count:", labels.length);
    console.log("Labels:", labels);
    console.log("Units:", units);
    console.log("Color Mapping:", brandColorMap);
    console.groupEnd();
  }

  const chartData = {
    labels,
    datasets: [
      {
        label: "Units",
        data: units,
        backgroundColor: backgroundColors,
        borderWidth: 0,
      },
    ],
  };

  // Calculate total for percentage display
  const total = units.reduce((sum, val) => sum + val, 0);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          boxWidth: 12,
          boxHeight: 12,
          color: "#6B7280",
        },
      },
      tooltip: {
        callbacks: {
          label: function (context: TooltipItem<"pie">) {
            const label = context.label || "";
            const value = toFiniteNumber(context.parsed);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value.toLocaleString()} (${percentage}%)`;
          },
        },
      },
      datalabels: {
        display: true,
        color: "#fff",
        font: {
          weight: "bold" as const,
          size: 14,
        },
        formatter: function (value: unknown) {
          const percentage = ((toFiniteNumber(value) / total) * 100).toFixed(0);
          // Only show label if percentage is > 5%
          return parseFloat(percentage) > 5 ? percentage + "%" : "";
        },
      },
    },
    cutout: "50%",
  } as const;

  return (
    <div className="w-full">
      {title && (
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
          {title}
        </h3>
      )}
      <div className="h-56 w-full">
        <Pie data={chartData} options={options} />
      </div>
    </div>
  );
};
